/**
 * 指導計画 (teaching plan) の表示・判定ヘルパー
 *
 * ラベル生成と必須判定は chaild-api の
 * src/component/nursery/teachingPlan/utils.ts と同じ規則にする
 * (サーバの通知本文・Excel出力と画面表示を一致させるため)。
 */
import dayjs from "dayjs";
import {
  NurseryClassResponse,
  NurseryTeachingPlanLayout,
  NurseryTeachingPlanLayoutCell,
  NurseryTeachingPlanListItem,
  NurseryTeachingPlanType,
} from "chaild-api/lib";
import { cellValueMaxLengths, planTypeLabels } from "@/constants/teachingPlan";

/** 年度と月から暦年を求める (年度は4月始まり) */
const calendarYearOfMonth = (businessYear: number, month: number): number =>
  month >= 4 ? businessYear : businessYear + 1;

export default class TeachingPlanUtil {
  /** YYYY/MM/DD 表記 */
  public static formatDate(value: string | null): string {
    if (!value) {
      return "";
    }
    const date = dayjs(value);
    return date.isValid() ? date.format("YYYY/MM/DD") : "";
  }

  /** YYYY/MM/DD HH:mm 表記 */
  public static formatDateTime(value: string | null): string {
    if (!value) {
      return "";
    }
    const date = dayjs(value);
    return date.isValid() ? date.format("YYYY/MM/DD HH:mm") : "";
  }

  /**
   * 週案・日案の対象期間ラベル。
   * 開始日=終了日 (1日分=日案) は単一日で表示し、同年内なら終了日の年を省く。
   */
  public static periodRangeLabel(
    periodStartDate: string | null,
    periodEndDate: string | null
  ): string {
    if (!periodStartDate) {
      return "";
    }
    const start = dayjs(periodStartDate);
    if (!start.isValid()) {
      return "";
    }
    const startLabel = start.format("YYYY年M月D日");
    if (!periodEndDate || periodEndDate === periodStartDate) {
      return startLabel;
    }
    const end = dayjs(periodEndDate);
    if (!end.isValid()) {
      return startLabel;
    }
    const endLabel =
      end.year() === start.year() ? end.format("M月D日") : end.format("YYYY年M月D日");
    return `${startLabel}〜${endLabel}`;
  }

  /** 一覧の「対象期間」列 */
  public static periodLabel(plan: {
    planType: NurseryTeachingPlanType;
    businessYear: number;
    month: number | null;
    periodStartDate: string | null;
    periodEndDate: string | null;
  }): string {
    switch (plan.planType) {
      case "monthly": {
        if (plan.month === null) {
          return `${plan.businessYear}年度`;
        }
        return `${calendarYearOfMonth(plan.businessYear, plan.month)}年${plan.month}月`;
      }
      case "weekly":
        return this.periodRangeLabel(plan.periodStartDate, plan.periodEndDate);
      default:
        return `${plan.businessYear}年度`;
    }
  }

  /** 帳票の見出しラベル (サーバの buildTeachingPlanLabel と同形) */
  public static planLabel(plan: {
    planType: NurseryTeachingPlanType;
    businessYear: number;
    month: number | null;
    periodStartDate: string | null;
    periodEndDate: string | null;
    className: string | null;
  }): string {
    const typeLabel = planTypeLabels[plan.planType];
    const classPart = `（${plan.className ?? "園全体"}）`;
    switch (plan.planType) {
      case "overall":
        return `${plan.businessYear}年度 ${typeLabel}`;
      case "annual":
        return `${plan.businessYear}年度 ${typeLabel}${classPart}`;
      case "monthly":
        return `${calendarYearOfMonth(plan.businessYear, plan.month ?? 4)}年${
          plan.month
        }月 ${typeLabel}${classPart}`;
      case "weekly":
        return `${this.periodRangeLabel(
          plan.periodStartDate,
          plan.periodEndDate
        )} ${typeLabel}${classPart}`;
      default:
        return typeLabel;
    }
  }

  /** 一覧行から帳票ラベルを組み立てる */
  public static listItemLabel(plan: NurseryTeachingPlanListItem): string {
    return this.planLabel(plan);
  }

  /** 入力セルか (label / reference は値を持たない) */
  public static isInputCell(cell: NurseryTeachingPlanLayoutCell): boolean {
    return cell.type !== "label" && cell.type !== "reference";
  }

  /**
   * 提出前の必須未入力チェック (api-design.md §1.1)。
   * required かつ visible の入力セルで値が空のセルを返す。
   * 契約上「提出モーダルの事前表示はフロント判定」とされているため画面側で持つ。
   */
  public static findMissingRequiredCells(
    layout: NurseryTeachingPlanLayout | null,
    values: { [itemKey: string]: { value: string | null } }
  ): { itemKey: string; name: string }[] {
    if (!layout) {
      return [];
    }
    const missing: { itemKey: string; name: string }[] = [];
    for (const row of layout.rows) {
      for (const cell of row.cells) {
        if (!cell.itemKey || !cell.required || !cell.visible || !this.isInputCell(cell)) {
          continue;
        }
        const value = values[cell.itemKey] ? values[cell.itemKey].value : null;
        if (value === null || value === "") {
          missing.push({ itemKey: cell.itemKey, name: cell.name });
        }
      }
    }
    return missing;
  }

  /** セル値の文字数上限 (上限を持たない型は null) */
  public static cellValueMaxLength(cell: NurseryTeachingPlanLayoutCell): number | null {
    return cellValueMaxLengths[cell.type] ?? null;
  }

  /**
   * セル値がサーバの検証 (validateTeachingPlanCellValue) を通るか。
   * 通らない場合はその理由を返す (空文字なら問題なし)。
   *
   * 自動保存は silentError なので、ここで弾かずに送ると
   * 「保存に失敗しました」だけが残って理由も直し方も分からなくなる。
   */
  public static cellValueError(
    cell: NurseryTeachingPlanLayoutCell,
    value: string
  ): string {
    // 空文字は「値を消す」操作。どの型でも許容される (サーバ側でnullになる)
    if (value === "") {
      return "";
    }
    const max = this.cellValueMaxLength(cell);
    switch (cell.type) {
      case "longText":
        return max !== null && value.length > max
          ? `${max.toLocaleString()}文字を${(value.length - max).toLocaleString()}文字超えています。`
          : "";
      case "shortText":
        if (value.includes("\n")) {
          return "短文セルには改行を入力できません。";
        }
        return max !== null && value.length > max
          ? `${max}文字を${value.length - max}文字超えています。`
          : "";
      case "number":
        if (!/^-?\d+(\.\d+)?$/.test(value)) {
          return "数値で入力してください。";
        }
        return max !== null && value.length > max ? `${max}文字以内で入力してください。` : "";
      case "select":
        // 通常はコントロールが値域を保証するが、レイアウトから選択肢が消されると
        // 保存済みの値が候補外になる。サーバは弾くので画面でも理由を出す
        return (cell.options || []).includes(value)
          ? ""
          : "この選択肢はレイアウトから削除されています。選び直してください。";
      case "date":
        // input[type=date] が使えないブラウザでは text にフォールバックする。
        // サーバと同じく「正規化しても同じ日付か」まで見る (2026-02-30 を弾く)
        return /^\d{4}-\d{2}-\d{2}$/.test(value) &&
          dayjs(value).isValid() &&
          dayjs(value).format("YYYY-MM-DD") === value
          ? ""
          : "日付は YYYY-MM-DD の実在する日で入力してください。";
      default:
        return "";
    }
  }

  /** レイアウト内の phraseKey をレイアウト上の出現順に重複なく集める */
  public static collectPhraseKeys(layout: NurseryTeachingPlanLayout | null): string[] {
    if (!layout) {
      return [];
    }
    const keys: string[] = [];
    for (const row of layout.rows) {
      for (const cell of row.cells) {
        if (cell.phraseKey && !keys.includes(cell.phraseKey)) {
          keys.push(cell.phraseKey);
        }
      }
    }
    return keys;
  }

  /** レイアウト内の入力セル一覧 (転記ルールの選択肢用) */
  public static collectInputCells(
    layout: NurseryTeachingPlanLayout | null
  ): NurseryTeachingPlanLayoutCell[] {
    if (!layout) {
      return [];
    }
    const cells: NurseryTeachingPlanLayoutCell[] = [];
    for (const row of layout.rows) {
      for (const cell of row.cells) {
        if (cell.itemKey && cell.type !== "label") {
          cells.push(cell);
        }
      }
    }
    return cells;
  }

  /** 列幅比率から百分率を求める (グリッド描画用) */
  public static widthPercents(widths: number[]): number[] {
    const total = widths.reduce((sum, width) => sum + (width > 0 ? width : 0), 0);
    if (total <= 0) {
      return widths.map(() => 100 / Math.max(widths.length, 1));
    }
    return widths.map((width) => ((width > 0 ? width : 0) / total) * 100);
  }

  /**
   * itemKey の形式検証 (api-design.md §3.2)。
   * 半角英数字始まり・英数字とハイフン/アンダースコアのみ・64文字以内。
   */
  public static isValidItemKey(itemKey: string): boolean {
    return /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/.test(itemKey);
  }

  /**
   * 転記ルールの対応記号 (0=A, 25=Z, 26=AA...)。
   * 転記元・転記先の両グリッドに同じ記号を出して対応関係を追えるようにする。
   */
  public static transferSymbol(index: number): string {
    let remaining = index;
    let symbol = "";
    do {
      symbol = String.fromCharCode(65 + (remaining % 26)) + symbol;
      remaining = Math.floor(remaining / 26) - 1;
    } while (remaining >= 0);
    return symbol;
  }

  /** レイアウト上の出現順に itemKey を並べる (記号の採番順に使う) */
  public static itemKeyOrder(layout: NurseryTeachingPlanLayout | null): string[] {
    if (!layout) {
      return [];
    }
    const keys: string[] = [];
    for (const row of layout.rows) {
      for (const cell of row.cells) {
        if (cell.itemKey) {
          keys.push(cell.itemKey);
        }
      }
    }
    return keys;
  }

  /**
   * 全年度のクラスが並ぶ選択肢向けのラベル。
   * 職員は過年度のクラスにも紐付いたまま残り、同名クラスが年度違いで並ぶため年度を添える。
   * 年度未設定の古いクラスは「年度未設定」と明示して現行クラスと区別する。
   */
  public static classLabel(item: Pick<NurseryClassResponse, "className" | "year">): string {
    return item.year === null ? `年度未設定 ${item.className}` : `${item.year}年度 ${item.className}`;
  }
}
